import { fetchJson } from "../lib/api";

export type ProjectSummary = {
  project_key: string;
  project_code: string | null;
  project_code_raw: string | null;
  project_kind: string | null;
  display_name: string | null;
  customer_raw?: string | null;
  information_raw?: string | null;
  source_date?: string | null;
  source?: string | null;
  active: boolean | null;
  total_spend: string | number | null;
  spend_ytd?: string | number | null;
  spend_last_year?: string | number | null;
  spend_last30?: string | number | null;
  line_count: number | null;
  distinct_articles: number | null;
  first_month_start: string | null;
  last_month_start: string | null;
  month_count: number | null;
  total_overpay?: string | number | null;
  max_abs_delta_pct?: string | number | null;
  delivery_address_count?: number | null;
  project_role?: "project" | "reporting_group" | string | null;
};

export type ProjectListSort =
  | "spend_desc"
  | "spend_last_year_desc"
  | "spend_ytd_desc"
  | "spend_last30_desc"
  | "overpay_desc"
  | "recent_desc";

export type ProjectListResponse = {
  total: number;
  limit: number;
  offset: number;
  with_overpay?: boolean;
  summary?: {
    project_count: number | string | null;
    total_spend: string | number | null;
    spend_ytd: string | number | null;
    spend_last_year: string | number | null;
    spend_last30: string | number | null;
    line_count: number | string | null;
    delivery_address_count: number | string | null;
  };
  items: ProjectSummary[];
};

export type ProjectOverpaySummary = {
  project_key: string;
  total_overpay: string | number | null;
  max_abs_delta_pct: string | number | null;
  delivery_address_count: number | null;
};

export type ProjectSpendMonth = {
  month_start: string | null;
  total_spend: string | number | null;
  line_count: number | null;
  avg_discount_pct: string | number | null;
  distinct_articles: number | null;
  cumulative_spend: string | number | null;
  cumulative_line_count: number | null;
};

export type ProjectPriceDrift = {
  article_group_key: string;
  primary_article_code: string | null;
  month_start: string | null;
  project_price: string | number | null;
  global_price: string | number | null;
  drift_abs: string | number | null;
  drift_pct: string | number | null;
  project_month_spend: string | number | null;
  project_month_lines: number | null;
};

export type ProjectSpendResponse = {
  project_key: string;
  total: number;
  limit: number;
  offset: number;
  items: ProjectSpendMonth[];
};

export type ProjectPriceDriftResponse = {
  project_key: string;
  total: number;
  limit: number;
  offset: number;
  items: ProjectPriceDrift[];
};

export async function fetchProject(
  projectKey: string,
): Promise<ProjectSummary> {
  return fetchJson<ProjectSummary>(
    `/api/projects/${encodeURIComponent(projectKey)}`,
  );
}

export async function fetchProjects(
  {
    activeOnly = true,
    limit = 100,
    offset = 0,
    sort,
    q,
    withOverpay,
  }: {
    activeOnly?: boolean;
    limit?: number;
    offset?: number;
    sort?: ProjectListSort;
    q?: string;
    withOverpay?: boolean;
  } = {},
): Promise<ProjectListResponse> {
  const params = new URLSearchParams({
    active_only: String(activeOnly),
    limit: String(limit),
    offset: String(offset),
  });
  if (sort) params.set("sort", sort);
  if (q && q.trim()) params.set("q", q.trim());
  if (withOverpay !== undefined) params.set("with_overpay", String(withOverpay));
  return fetchJson<ProjectListResponse>(`/api/projects?${params.toString()}`);
}

export async function fetchProjectsOverpaySummary(): Promise<{
  items: ProjectOverpaySummary[];
}> {
  return fetchJson<{ items: ProjectOverpaySummary[] }>(
    "/api/projects/_overpay-summary",
  );
}

export async function fetchProjectSpendMonthly(
  projectKey: string,
  limit = 240,
  offset = 0,
): Promise<ProjectSpendResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return fetchJson<ProjectSpendResponse>(
    `/api/projects/${encodeURIComponent(projectKey)}/spend-monthly?${params.toString()}`,
  );
}

export async function fetchProjectPriceDrift(
  projectKey: string,
  limit = 100,
  offset = 0,
  minAbsDriftPct?: number,
): Promise<ProjectPriceDriftResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (minAbsDriftPct !== undefined) {
    params.set("min_abs_drift_pct", String(minAbsDriftPct));
  }
  return fetchJson<ProjectPriceDriftResponse>(
    `/api/projects/${encodeURIComponent(projectKey)}/price-drift?${params.toString()}`,
  );
}
